/**
 * EntityPageHeader Component
 * 
 * A header for entity detail/edit pages with title, subtitle, status, and action buttons.
 * Designed for Company, Reseller, Location, System, Employee pages.
 * Responsive design with stacked layout on mobile.
 */

import React from 'react';
import { Box, Typography, CircularProgress, Skeleton, alpha, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export type EntityType = 'company' | 'reseller' | 'location' | 'system' | 'employee';

export interface EntityPageHeaderProps {
  /** Entity type for styling */
  entityType: EntityType;
  /** Page title */
  title: string;
  /** Entity ID */
  id?: string | number;
  /** Subtitle or status text */
  subtitle?: string;
  /** Status badge to display next to ID (e.g., "Archived" chip) */
  statusBadge?: React.ReactNode;
  /** Icon component */
  icon?: React.ReactNode;
  /** Whether the page is in create mode */
  isCreate?: boolean;
  /** Whether data is loading */
  loading?: boolean;
  /** Save button handler */
  onSave?: () => void;
  /** Cancel/back button handler */
  onCancel?: () => void;
  /** Whether save is disabled */
  saveDisabled?: boolean;
  /** Save button text */
  saveText?: string;
  /** Whether save is in progress */
  saving?: boolean;
  /** Additional actions */
  actions?: React.ReactNode;
  /** Additional styles */
  sx?: SxProps<Theme>;
}

const entityColors: Record<EntityType, string> = {
  company: colors.primary[500],
  reseller: colors.info.main,
  location: colors.success.main,
  system: colors.primary[500],
  employee: colors.warning.main,
};

export const EntityPageHeader: React.FC<EntityPageHeaderProps> = ({
  entityType,
  title,
  id,
  subtitle,
  statusBadge,
  icon,
  isCreate = false,
  loading = false,
  onSave,
  onCancel,
  saveDisabled = false,
  saveText,
  saving = false,
  actions,
  sx,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentColor = entityColors[entityType];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          ...sx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="rounded" width={isMobile ? 40 : 48} height={isMobile ? 40 : 48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={isMobile ? 150 : 200} height={28} />
            <Skeleton variant="text" width={100} height={18} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" width={90} height={36} />
          <Skeleton variant="rounded" width={110} height={36} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        justifyContent: 'space-between',
        gap: isMobile ? 2 : 3,
        mb: 3,
        ...sx,
      }}
    >
      {/* Left side: Back button, Icon, Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2, minWidth: 0 }}>
        {/* Back Button */}
        {onCancel && (
          <IconButton
            icon={<ArrowBackIcon />}
            onClick={onCancel}
            variant="ghost"
            size={isMobile ? 'small' : 'medium'}
            aria-label="Go back"
          />
        )}

        {/* Icon */}
        {icon && (
          <Box
            sx={{
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              borderRadius: `${radii.lg}px`,
              backgroundColor: alpha(accentColor, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accentColor,
              flexShrink: 0,
              '& svg': {
                fontSize: isMobile ? 20 : 24,
              },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Title and Subtitle */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typographyTokens.fontFamily.display,
              fontWeight: typographyTokens.fontWeight.bold,
              fontSize: isMobile ? typographyTokens.fontSize.lg : typographyTokens.fontSize['2xl'],
              color: colors.neutral[900],
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'nowrap' : 'normal',
            }}
          >
            {title}
          </Typography>

          {(id || subtitle || statusBadge) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {id && !isCreate && (
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.neutral[500],
                    fontFamily: typographyTokens.fontFamily.mono,
                    fontSize: typographyTokens.fontSize.xs,
                  }}
                >
                  ID: {id}
                </Typography>
              )}
              {statusBadge}
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.neutral[600],
                    fontSize: typographyTokens.fontSize.xs,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Right side: Actions */}
      {isMobile ? (
        // Mobile: Stack with Save on top (full width), then other actions below
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          {onSave && (
            <Button
              variant="primary"
              onClick={onSave}
              disabled={saveDisabled || saving}
              loading={saving}
              fullWidth
            >
              {saveText || (isCreate ? 'Create' : 'Save Changes')}
            </Button>
          )}
          {actions && (
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 1,
                width: '100%',
                // Handle both direct buttons and wrapped buttons (e.g., in Stack)
                '& > *': { 
                  flex: 1,
                  display: 'flex',
                  gap: 1,
                },
                '& .MuiButton-root': { 
                  minHeight: 36,
                  flex: 1,
                },
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      ) : (
        // Desktop: All actions in a row
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexShrink: 0,
          }}
        >
        {actions}

        {onSave && (
          <Button
            variant="primary"
            onClick={onSave}
            disabled={saveDisabled || saving}
            loading={saving}
          >
            {saveText || (isCreate ? 'Create' : 'Save Changes')}
          </Button>
        )}
      </Box>
      )}
    </Box>
  );
};

export default EntityPageHeader;

